"use client"

import type { AnalyticsEvent, EventPropertyMap } from "./events"

const ACTIVATION_TARGET_MESSAGES = 5
const ACTIVATION_WINDOW_MS = 24 * 60 * 60 * 1000
const STORAGE_PREFIX = "pleasurai.activation"

type ActivationSourceEvent =
  | "character:creation_complete"
  | "chat:message_send"

type ActivationState = {
  userId: string
  signupAt: number
  characterCreated: boolean
  chatMessageCount: number
  activated: boolean
}

type ActivationPostHog = {
  capture: (event: string, properties?: Record<string, unknown>) => void
  identify?: (distinctId: string, properties?: Record<string, unknown>) => void
}

let currentUser: { userId: string; signupAt: number } | null = null
const memoryStateByUser = new Map<string, ActivationState>()

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}.${userId}`
}

function isActivationSourceEvent(event: AnalyticsEvent): event is ActivationSourceEvent {
  return event === "character:creation_complete" || event === "chat:message_send"
}

function isWithinActivationWindow(signupAt: number, now: number): boolean {
  return now >= signupAt && now - signupAt <= ACTIVATION_WINDOW_MS
}

function readState(userId: string, signupAt: number): ActivationState {
  const initial: ActivationState = {
    userId,
    signupAt,
    characterCreated: false,
    chatMessageCount: 0,
    activated: false,
  }
  const memoryState = memoryStateByUser.get(userId)
  const fallback = memoryState ? { ...memoryState, signupAt } : initial

  if (typeof window === "undefined") return fallback

  try {
    const raw = window.localStorage.getItem(storageKey(userId))
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as Partial<ActivationState>
    if (parsed.userId !== userId) return fallback

    return {
      ...initial,
      signupAt,
      characterCreated: parsed.characterCreated === true,
      chatMessageCount:
        typeof parsed.chatMessageCount === "number"
          ? Math.max(0, parsed.chatMessageCount)
          : 0,
      activated: parsed.activated === true,
    }
  } catch {
    return fallback
  }
}

function writeState(state: ActivationState): void {
  memoryStateByUser.set(state.userId, state)
  if (typeof window === "undefined") return

  try {
    window.localStorage.setItem(storageKey(state.userId), JSON.stringify(state))
  } catch {
    // Analytics must never block the product path.
  }
}

function nextActivationState(
  state: ActivationState,
  event: ActivationSourceEvent,
  now: number
): ActivationState {
  if (state.activated || !isWithinActivationWindow(state.signupAt, now)) {
    return state
  }

  const next = {
    ...state,
    characterCreated:
      state.characterCreated || event === "character:creation_complete",
    chatMessageCount:
      event === "chat:message_send"
        ? state.chatMessageCount + 1
        : state.chatMessageCount,
  }

  return {
    ...next,
    activated:
      next.characterCreated &&
      next.chatMessageCount >= ACTIVATION_TARGET_MESSAGES,
  }
}

function captureActivationComplete(ph: ActivationPostHog, userId: string): void {
  ph.identify?.(userId, { is_activated: true })
  ph.capture("activation_complete", {
    is_activated: true,
    $set: { is_activated: true },
  })
}

export function configureActivationTracking(
  userId: string,
  signupAt: string | number | Date
): void {
  const parsedSignupAt = new Date(signupAt).getTime()
  currentUser = Number.isFinite(parsedSignupAt)
    ? { userId, signupAt: parsedSignupAt }
    : null
}

export function clearActivationTracking(): void {
  currentUser = null
}

export function getCurrentActivationChatMessageCount(): number {
  if (!currentUser) return 0
  return readState(currentUser.userId, currentUser.signupAt).chatMessageCount
}

export function handleActivationSourceEvent<E extends AnalyticsEvent>(
  ph: ActivationPostHog,
  event: E,
  _properties: EventPropertyMap[E],
  now = Date.now()
): void {
  if (!currentUser || !isActivationSourceEvent(event)) return

  const state = readState(currentUser.userId, currentUser.signupAt)
  const next = nextActivationState(state, event, now)
  if (next === state) return

  if (!state.activated && next.activated) {
    try {
      captureActivationComplete(ph, currentUser.userId)
    } catch {
      writeState({ ...next, activated: false })
      return
    }
  }

  writeState(next)
}

export const __activationTrackingTest = {
  ACTIVATION_TARGET_MESSAGES,
  ACTIVATION_WINDOW_MS,
  memoryStateByUser,
  nextActivationState,
}
